import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { BarChart3, LogOut, Menu, ShieldCheck, UserCheck } from "lucide-react";
import {
  TailClipboardIcon,
  TailDashboardIcon,
  TailUsersIcon,
} from "@/components/template/tailadmin/TailAdminIcons";
import { setAdminToken } from "@/services/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    setAdminToken(null);
    toast.success("Logged out successfully");
    navigate("/admin/login");
  };

  const navItems = [
    { to: "/admin/dashboard", icon: TailDashboardIcon, label: "Dashboard" },
    { to: "/admin/clients", icon: TailUsersIcon, label: "Clients" },
    { to: "/admin/brms", icon: UserCheck, label: "BRMs" },
    { to: "/admin/enrollments", icon: TailClipboardIcon, label: "Enrollments" },
    { to: "/admin/analytics", icon: BarChart3, label: "Analytics" },
  ];

  const NavLinks = ({ mobile = false }) => (
    <nav className="flex flex-col gap-1">
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={() => mobile && setIsMobileMenuOpen(false)}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition",
              isActive
                ? "bg-brand-50 text-brand-500"
                : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            )
          }
        >
          <item.icon className="h-5 w-5" />
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-800">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[290px] border-r border-gray-200 bg-white px-5 lg:block">
        <div className="flex h-20 items-center">
          <button onClick={() => navigate("/admin/dashboard")} className="flex items-center gap-3 text-left">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-500 text-white shadow-theme-xs">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-lg font-semibold text-gray-900">Proovam</span>
              <span className="block text-xs text-gray-500">Admin workspace</span>
            </span>
          </button>
        </div>

        <div className="mb-4 text-xs font-medium uppercase leading-5 text-gray-400">Administration</div>
        <NavLinks />

        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 p-5">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="h-11 w-full justify-start text-gray-500 hover:bg-[#fef3f2] hover:text-[#d92d20]"
          >
            <LogOut className="h-5 w-5" />
            Logout
          </Button>
        </div>
      </aside>

      <div className="lg:pl-[290px]">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-gray-200 bg-white/95 px-4 backdrop-blur md:px-6">
          <button className="flex items-center gap-2 lg:hidden" onClick={() => navigate("/admin/dashboard")}>
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-500 text-white">
              <ShieldCheck className="h-4 w-4" />
            </span>
            <span className="font-semibold text-gray-900">Proovam Admin</span>
          </button>

          <div className="hidden lg:block">
            <p className="text-sm font-medium text-gray-700">Admin workspace</p>
            <p className="text-xs text-gray-500">Manage clients, BRMs, enrollments, and platform analytics.</p>
          </div>

          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="lg:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 border-gray-200 bg-white p-0">
              <div className="flex h-full flex-col">
                <div className="border-b border-gray-200 p-5">
                  <h2 className="font-semibold text-gray-900">Navigation</h2>
                </div>
                <div className="flex-1 p-5">
                  <NavLinks mobile />
                </div>
                <div className="border-t border-gray-200 p-5">
                  <Button onClick={handleLogout} variant="ghost" className="w-full justify-start text-gray-500 hover:bg-[#fef3f2] hover:text-[#d92d20]">
                    <LogOut className="h-5 w-5" />
                    Logout
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </header>

        <main className="min-h-[calc(100vh-64px)]">
          <div className="mx-auto max-w-screen-2xl p-4 md:p-6">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
